/**
 * Application entry point.
 *
 * Loads persisted state, registers the global handlers referenced by the HTML's
 * inline attributes (by spreading each feature/document module onto `window`),
 * wires page-enter hooks and privacy lifecycle, kicks off async asset loads,
 * and runs the initial render.
 */

import { store } from '@/app/store';
import { byId } from '@/ui/dom';
import { onPageEnter, onPageLeave, showPage, goBack } from '@/ui/router';
import { showToast, closeModal, confirmResolve, showModal } from '@/ui/overlays';
import { loadMglSearch } from '@/app/mgl';
import { installPrivacyHooks, wipeTransientData } from '@/app/lifecycle';
import { installOfflineSync, syncOfflineData, renderSyncStatus } from '@/app/offline-sync';
import {
  applyStoredContentUpdate,
  installContentUpdateCheck,
  checkForContentUpdate,
  downloadContentUpdate,
  declineContentUpdate,
  renderContentUpdateStatus
} from '@/app/content-updates';

import { setSide, setPanelLens, resetPanelLens } from '@/features/side';
import * as panel from '@/features/panel';
import * as panelIO from '@/features/panel-io';
import * as caseTab from '@/features/case';
import * as charges from '@/features/charges';
import * as scan from '@/features/scan';
import { svSetEvidence } from '@/features/juror-sv';
import { renderCaseProfile, setCaseProfileField } from '@/features/case-profile';
import { setVdRating } from '@/features/vd-capture';
import * as parties from '@/features/parties';
import * as vd from '@/features/vd';
import * as research from '@/features/research';
import * as legal from '@/features/legal';
import * as law from '@/features/law';
import * as lawOpinions from '@/features/law-opinions';
import * as lawRules from '@/features/law-rules';
import * as lawCite from '@/features/law-cite';
import * as citationReview from '@/features/citation-review';
import * as docViewer from '@/documents/doc-viewer';
import * as docActions from '@/documents/actions';
import * as reports from '@/documents/reports';
import * as liveResearch from '@/services/live-research';

/** VD question tap toggle (small dynamic handler). */
function tapVDQ(id: number | string): void {
  const el = byId('vdq-' + id);
  if (!el) return;
  el.classList.toggle('tapped');
  showToast(el.classList.contains('tapped') ? 'Question marked' : 'Question unmarked', 1400);
}

function registerGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  Object.assign(
    w,
    {
      showPage,
      goBack,
      showToast,
      showModal,
      closeModal,
      confirmResolve,
      setSide,
      setPanelLens,
      tapVDQ,
      wipeTransientData,
      svSetEvidence,
      setCaseProfileField,
      setVdRating,
      syncOfflineData,
      renderSyncStatus,
      checkForContentUpdate,
      downloadContentUpdate,
      declineContentUpdate
    },
    panel,
    panelIO,
    caseTab,
    charges,
    scan,
    parties,
    vd,
    research,
    legal,
    law,
    lawOpinions,
    lawRules,
    lawCite,
    citationReview,
    docViewer,
    docActions,
    reports,
    liveResearch
  );
}

function registerPageHooks(): void {
  onPageEnter('case', () => {
    caseTab.syncCaseForm();
    renderCaseProfile();
    renderSyncStatus();
    renderContentUpdateStatus();
  });
  onPageEnter('panel', panel.renderPanel);
  onPageEnter('vd', vd.renderVD);
  onPageEnter('research', research.renderResearch);
  onPageEnter('law', () => {
    law.renderLawTab();
    citationReview.renderCitationReview();
  });
  onPageEnter('legal', () => legal.showLegalTab(legal.getLegalActiveTab() || 'motion'));
  // Release any live camera (desktop webcam / facts camera) when leaving Scan.
  onPageLeave('scan', scan.stopCamera);
}

function init(): void {
  store.load();
  // Apply any previously-downloaded-and-accepted legal-content update before
  // anything reads CASELAW — synchronous, offline, no network dependency.
  applyStoredContentUpdate();
  registerGlobals();
  registerPageHooks();
  installPrivacyHooks();
  // Case fields persist as you type. Before this, the only form -> state path
  // was the Save Case button, so anything typed and not explicitly saved was
  // gone on the next launch.
  caseTab.installCaseAutosave();

  // Paints both side echoes and drops any lens that survived in persisted state:
  // the lens is transient and must never outlive a relaunch. (Was setSide(), which
  // also fired a toast on every launch.)
  resetPanelLens();
  panel.updatePanelBadge();
  showPage('case', false);

  // Load MGL statute index (for statute lookup / Law tab) — non-blocking.
  void loadMglSearch();
  // Merge the prebaked statute library into CASELAW, then refresh the Law tab.
  void liveResearch
    .lrLoadStatuteLibrary()
    .then(() => law.renderLawTab())
    .catch(() => {
      /* statute library unavailable offline */
    });

  // Reliable internet only ever exists OUTSIDE a court day for this app's
  // users (see [[offline-readiness]]) — proactively pull down every statute,
  // the case-law/rules DB, and both OCR engines the moment real connectivity
  // is detected, rather than waiting for the attorney to browse into each
  // feature (which may never happen before they lose connectivity again).
  installOfflineSync();
  // Check for a legal-content update (case-law/statute/rule corrections and
  // additions published independently of an app release) whenever online.
  // Never applied automatically — the attorney downloads or declines it.
  installContentUpdateCheck();

  // Resume background re-OCR of any illegible fields left from a prior session.
  // Cheap IndexedDB check first, so the heavy recognizer chunk only loads if
  // there's actually pending work.
  void import('@/ocr/field-review').then(async (fr) => {
    if (await fr.hasPending()) {
      const engine = await import('@/ocr/field-review-engine');
      void engine.startFieldReview();
    }
  });

  // Register the service worker in production web builds only. Inside a native
  // Capacitor WebView the app shell is already bundled on-device, so the SW is
  // unnecessary and its caching can serve stale assets after an app update.
  const isNativeApp = Boolean((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
  if (import.meta.env.PROD && !isNativeApp && 'serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('SW registration failed:', e));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
